import { Navigate, RouteObject } from "react-router-dom";

import { useAuth } from "../hooks/useAuth";
import { AdminLayout } from "../layout/components/AdminLayout";
import { DashboardLayout } from "../layout/components/DashboardLayout";
import { ReportsLayout } from "../layout/components/ReportsLayout";
import {
  AdminAlerts,
  AdminHome,
  AdminOverview,
  AdminRolePermissions,
  AdminUserAccess,
  AdminBillableDevices,
  AdminCellularDevices,
  AdminFirmware,
  AdminEMedBeaumont,
  AdminIntegrationApiKeys,
  AdminIntegrationQueue,
  AdminAiPromptTemplates,
  AdminAuditLogs,
  AdminArchivedPatients,
  AdminBaselines,
  AdminPatientDetails,
  AdminCognitoMigration,
  AdminPatientsList,
  AdminQuartzJobs,
  AdminEmailTemplates,
  AdminJupyterIntegration,
  AdminTmpIntegration,
  AdminDexcomIntegration,
  AdminMeasurements,
  AdminMedications,
  AdminSamMedications,
  AdminCustomMedications,
  AdminStaffDetails,
  AdminStaffList,
  AdminStudiesList,
  AdminMasterTranslations,
  AdminSurveys,
  AdminSurveyTranslations,
  AdminTranslationsSync,
  AdminHospitalDetails,
  AdminHospitalsList,
  AdminTenantDetails,
  AdminTenantsList,
  AdminOneMstsTests,
  AdminSpirometrySessionDetails,
  AdminSpirometrySessions,
  AdminTreatments,
  AdminWardDetails,
  AdminWardMeasurementTypes,
  AdminWardsList,
  AdminWorkflows,
} from "../pages/Admin";
import { RequirePagePermission } from "../pages/Admin/shared";
import { AppointmentsPage } from "../pages/Appointments";
import { BillingPatientList } from "../pages/BillingPatientList";
import { CareInfo } from "../pages/CareInfo";
import { ArticleViewer } from "../pages/CareInfo/components/ArticleViewer";
import { Dashboard } from "../pages/Dashboard";
import { DexcomCallbackPage } from "../pages/DexcomCallback";
import { FitbitCallbackPage } from "../pages/FitbitCallback";
import { ForgotPasswordPage } from "../pages/ForgotPassword";
import { GoogleHealthCallbackPage } from "../pages/GoogleHealthCallback";
import { JupyterHealthExchangeCallbackPage } from "../pages/JupyterHealthExchangeCallback";
import { LoginPage } from "../pages/Login";
import { MFAPage } from "../pages/MFA";
import { NotFound, NotFoundContent } from "../pages/NotFound";
import { PatientList } from "../pages/PatientList";
import { PatientPage } from "../pages/PatientPage";
import { WorkflowsList } from "../pages/PatientWorkflows";
import { WorkflowsBuilder } from "../pages/PatientWorkflows/components/WorkflowsBuilder";
import { ProtocolList } from "../pages/Protocol";
import { ProtocolBuilder } from "../pages/Protocol/components/ProtocolBuilder";
import { RagConfigListPage, RagConfigPage } from "../pages/RagConfig";
import { SetPasswordPage } from "../pages/SetPassword";
import { CreatePassSuccessPage } from "../pages/SetPassword/CreatePassSuccessPage";
import { SettingsPage } from "../pages/Settings";
import { AnswerSurveyPage } from "../pages/Surveys/Answer Survey";
import { AssignedSurveysList } from "../pages/Surveys/Answer Survey/Components/AssignedSurveysList";
import { SurveyBuilder } from "../pages/Surveys/SurveyBuilder";
import { SurveysPage } from "../pages/Surveys/SurveyList/SurveyListPage";
import { TmpOnboardingPage } from "../pages/TmpOnboarding";
import { PERMISSIONS } from "../utils/hasPermission";
import { patientListChildRoutes, patientPageChildRoutes } from "./child.routes";

/**
 * EN-2750. Element for the catch-all route. A signed-in user keeps the real
 * portal chrome around the error block; without a session there is nothing to
 * wrap it in, and rendering DashboardLayout would bounce them to /login
 * instead of showing the page.
 *
 * Declared in this module on purpose — it needs DashboardLayout, and any
 * separate module importing that would close an import cycle back onto
 * `routes`, whose elements are built at module scope. A function declaration
 * here is hoisted, so it is always defined by the time `routes` is evaluated.
 */
function NotFoundRoute() {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <NotFound />;
  }

  return (
    <DashboardLayout leftPanelHidden>
      <NotFoundContent />
    </DashboardLayout>
  );
}

export const routes: RouteObject[] = [
  // Auth routes (no layout)
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/mfa",
    element: <MFAPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/set-password-success",
    element: <CreatePassSuccessPage />,
  },
  {
    path: "/set-password",
    element: <SetPasswordPage />,
  },
  {
    path: "/dexcom/callback",
    element: <DexcomCallbackPage />,
  },

  // Reports routes (EN-5129 — formerly /admin): dashboard with all reports.
  {
    path: "/reports",
    element: <ReportsLayout />,
    children: [
      {
        index: true,
        element: <AdminHome />,
      },
      {
        // Audit Logs lived here briefly during the EN-5129 restructure and
        // now belongs to the /admin section only.
        path: "settings/audit-logs",
        element: <Navigate to="/admin/settings/audit-logs" replace />,
      },
    ],
  },

  // Admin routes (EN-5129): in-portal replacement for the Retool internal
  // tools — SuperAdmin + @patientmpower.com only (enforced by AdminLayout).
  {
    path: "/admin",
    element: <AdminLayout />,
    children: [
      {
        index: true,
        element: <AdminOverview />,
      },
      {
        path: "devices/billable",
        element: (
          <RequirePagePermission
            permission={PERMISSIONS.BILLABLE_DEVICES_ACCESS}
          >
            <AdminBillableDevices />
          </RequirePagePermission>
        ),
      },
      {
        path: "devices/firmware",
        element: (
          <RequirePagePermission permission={PERMISSIONS.FIRMWARE_ACCESS}>
            <AdminFirmware />
          </RequirePagePermission>
        ),
      },
      {
        path: "devices/cellular",
        element: (
          <RequirePagePermission
            permission={PERMISSIONS.CELLULAR_DEVICES_ACCESS}
          >
            <AdminCellularDevices />
          </RequirePagePermission>
        ),
      },
      {
        path: "tenants",
        element: (
          <RequirePagePermission permission={PERMISSIONS.TENANTS_ACCESS}>
            <AdminTenantsList />
          </RequirePagePermission>
        ),
      },
      {
        path: "tenants/:tenantId",
        element: (
          <RequirePagePermission permission={PERMISSIONS.TENANTS_ACCESS}>
            <AdminTenantDetails />
          </RequirePagePermission>
        ),
      },
      {
        path: "hospitals",
        element: (
          <RequirePagePermission permission={PERMISSIONS.HOSPITALS_ACCESS}>
            <AdminHospitalsList />
          </RequirePagePermission>
        ),
      },
      {
        path: "hospitals/:hospitalId",
        element: (
          <RequirePagePermission permission={PERMISSIONS.HOSPITALS_ACCESS}>
            <AdminHospitalDetails />
          </RequirePagePermission>
        ),
      },
      {
        path: "wards",
        element: (
          <RequirePagePermission permission={PERMISSIONS.WARDS_ACCESS}>
            <AdminWardsList />
          </RequirePagePermission>
        ),
      },
      {
        // Before "wards/:wardId" — a static segment must not be swallowed by
        // the id route.
        path: "wards/measurement-types",
        element: (
          <RequirePagePermission
            permission={PERMISSIONS.WARD_MEASUREMENT_TYPES_ACCESS}
          >
            <AdminWardMeasurementTypes />
          </RequirePagePermission>
        ),
      },
      {
        path: "wards/:wardId",
        element: (
          <RequirePagePermission permission={PERMISSIONS.WARDS_ACCESS}>
            <AdminWardDetails />
          </RequirePagePermission>
        ),
      },
      {
        path: "staff",
        element: (
          <RequirePagePermission permission={PERMISSIONS.STAFF_ACCESS}>
            <AdminStaffList />
          </RequirePagePermission>
        ),
      },
      {
        path: "staff/:staffId",
        element: (
          <RequirePagePermission permission={PERMISSIONS.STAFF_ACCESS}>
            <AdminStaffDetails />
          </RequirePagePermission>
        ),
      },
      {
        path: "integrations/emed-beaumont",
        element: (
          <RequirePagePermission permission={PERMISSIONS.EMED_BEAUMONT_ACCESS}>
            <AdminEMedBeaumont />
          </RequirePagePermission>
        ),
      },
      {
        path: "integrations/queue",
        element: (
          <RequirePagePermission permission={PERMISSIONS.EMED_BEAUMONT_ACCESS}>
            <AdminIntegrationQueue />
          </RequirePagePermission>
        ),
      },
      {
        path: "integrations/jupyter",
        element: (
          <RequirePagePermission
            permission={PERMISSIONS.JUPYTER_INTEGRATION_ACCESS}
          >
            <AdminJupyterIntegration />
          </RequirePagePermission>
        ),
      },
      {
        // Machine credentials for the integration API, Retool and webhooks.
        path: "integrations/api-keys",
        element: (
          <RequirePagePermission
            permission={PERMISSIONS.INTEGRATION_API_KEYS_ACCESS}
          >
            <AdminIntegrationApiKeys />
          </RequirePagePermission>
        ),
      },
      {
        // EN-5203: Transmural Platform sessions + prescriber mappings.
        path: "integrations/tmp",
        element: (
          <RequirePagePermission
            permission={PERMISSIONS.TMP_INTEGRATION_ACCESS}
          >
            <AdminTmpIntegration />
          </RequirePagePermission>
        ),
      },
      {
        // EN-5232: connected Dexcom patients and their sync history.
        path: "integrations/dexcom",
        element: (
          <RequirePagePermission
            permission={PERMISSIONS.DEXCOM_INTEGRATION_ACCESS}
          >
            <AdminDexcomIntegration />
          </RequirePagePermission>
        ),
      },
      {
        path: "access/roles",
        element: (
          <RequirePagePermission
            permission={PERMISSIONS.ROLE_PERMISSIONS_ACCESS}
          >
            <AdminRolePermissions />
          </RequirePagePermission>
        ),
      },
      {
        path: "access/users",
        element: (
          <RequirePagePermission permission={PERMISSIONS.USER_ACCESS_ACCESS}>
            <AdminUserAccess />
          </RequirePagePermission>
        ),
      },
      {
        path: "studies",
        element: (
          <RequirePagePermission permission={PERMISSIONS.STUDIES_ACCESS}>
            <AdminStudiesList />
          </RequirePagePermission>
        ),
      },
      {
        path: "patients/treatments",
        element: (
          <RequirePagePermission permission={PERMISSIONS.TREATMENTS_ACCESS}>
            <AdminTreatments />
          </RequirePagePermission>
        ),
      },
      {
        path: "patients",
        element: (
          <RequirePagePermission permission={PERMISSIONS.PATIENTS_ACCESS}>
            <AdminPatientsList />
          </RequirePagePermission>
        ),
      },
      {
        path: "patients/archived",
        element: (
          <RequirePagePermission permission={PERMISSIONS.PATIENTS_ACCESS}>
            <AdminArchivedPatients />
          </RequirePagePermission>
        ),
      },
      {
        // EN-5222: bulk-migrate legacy-app patients to Cognito. Its own
        // permission — the page hands out passwords.
        path: "patients/cognito-migration",
        element: (
          <RequirePagePermission
            permission={PERMISSIONS.COGNITO_MIGRATION_ACCESS}
          >
            <AdminCognitoMigration />
          </RequirePagePermission>
        ),
      },
      {
        // Above the static patients/* routes would shadow them, so the
        // parameterised one is declared after; react-router ranks static
        // segments higher regardless, but the order keeps that readable.
        path: "patients/:patientId",
        element: (
          <RequirePagePermission permission={PERMISSIONS.PATIENTS_ACCESS}>
            <AdminPatientDetails />
          </RequirePagePermission>
        ),
      },
      {
        // EN-5240: the Medications menu group; the old single page is SNOMED.
        path: "medications",
        element: <Navigate to="/admin/medications/snomed" replace />,
      },
      {
        path: "medications/snomed",
        element: (
          <RequirePagePermission permission={PERMISSIONS.MEDICATIONS_ACCESS}>
            <AdminMedications />
          </RequirePagePermission>
        ),
      },
      {
        path: "medications/sam",
        element: (
          <RequirePagePermission permission={PERMISSIONS.MEDICATIONS_ACCESS}>
            <AdminSamMedications />
          </RequirePagePermission>
        ),
      },
      {
        path: "medications/custom",
        element: (
          <RequirePagePermission permission={PERMISSIONS.MEDICATIONS_ACCESS}>
            <AdminCustomMedications />
          </RequirePagePermission>
        ),
      },
      {
        path: "patients/measurements",
        element: (
          <RequirePagePermission permission={PERMISSIONS.MEASUREMENTS_ACCESS}>
            <AdminMeasurements />
          </RequirePagePermission>
        ),
      },
      {
        path: "patients/1msts",
        element: (
          <RequirePagePermission permission={PERMISSIONS.ONE_MSTS_ACCESS}>
            <AdminOneMstsTests />
          </RequirePagePermission>
        ),
      },
      {
        path: "patients/spirometry",
        element: (
          <RequirePagePermission permission={PERMISSIONS.SPIROMETRY_ACCESS}>
            <AdminSpirometrySessions />
          </RequirePagePermission>
        ),
      },
      {
        path: "patients/spirometry/:sessionId",
        element: (
          <RequirePagePermission permission={PERMISSIONS.SPIROMETRY_ACCESS}>
            <AdminSpirometrySessionDetails />
          </RequirePagePermission>
        ),
      },
      {
        path: "patients/baselines",
        element: (
          <RequirePagePermission permission={PERMISSIONS.BASELINES_ACCESS}>
            <AdminBaselines />
          </RequirePagePermission>
        ),
      },
      {
        path: "surveys",
        element: (
          <RequirePagePermission permission={PERMISSIONS.SURVEYS_ACCESS}>
            <AdminSurveys />
          </RequirePagePermission>
        ),
      },
      {
        path: "alerts",
        element: (
          <RequirePagePermission permission={PERMISSIONS.ALERTS_ACCESS}>
            <AdminAlerts />
          </RequirePagePermission>
        ),
      },
      {
        path: "workflows",
        element: (
          <RequirePagePermission permission={PERMISSIONS.WORKFLOWS_ACCESS}>
            <AdminWorkflows />
          </RequirePagePermission>
        ),
      },
      {
        path: "translations/surveys",
        element: (
          <RequirePagePermission
            permission={PERMISSIONS.SURVEY_TRANSLATIONS_ACCESS}
          >
            <AdminSurveyTranslations />
          </RequirePagePermission>
        ),
      },
      {
        path: "translations/keys",
        element: (
          <RequirePagePermission permission={PERMISSIONS.TRANSLATIONS_ACCESS}>
            <AdminMasterTranslations />
          </RequirePagePermission>
        ),
      },
      {
        path: "translations/sync",
        element: (
          <RequirePagePermission
            permission={PERMISSIONS.TRANSLATIONS_SYNC_ACCESS}
          >
            <AdminTranslationsSync />
          </RequirePagePermission>
        ),
      },
      {
        path: "email-templates",
        element: (
          <RequirePagePermission
            permission={PERMISSIONS.EMAIL_TEMPLATES_ACCESS}
          >
            <AdminEmailTemplates />
          </RequirePagePermission>
        ),
      },
      {
        path: "ai-prompt-templates",
        element: (
          <RequirePagePermission
            permission={PERMISSIONS.AI_PROMPT_TEMPLATES_ACCESS}
          >
            <AdminAiPromptTemplates />
          </RequirePagePermission>
        ),
      },
      {
        path: "settings/audit-logs",
        element: (
          <RequirePagePermission permission={PERMISSIONS.AUDIT_LOGS_READ}>
            <AdminAuditLogs />
          </RequirePagePermission>
        ),
      },
      {
        path: "settings/scheduled-jobs",
        element: (
          <RequirePagePermission permission={PERMISSIONS.QUARTZ_JOBS_ACCESS}>
            <AdminQuartzJobs />
          </RequirePagePermission>
        ),
      },
    ],
  },

  // Root redirect
  {
    path: "/fitbit/callback",
    element: <FitbitCallbackPage />,
  },
  {
    path: "/google-health/callback",
    element: <GoogleHealthCallbackPage />,
  },
  {
    path: "/jhe/callback",
    element: <JupyterHealthExchangeCallbackPage />,
  },
  // Transmural Platform onboarding completion (EN-5203) — standalone, no auth
  // guard. TMP redirects a hospital clinician here with a one-time code; they
  // have no portal account, so DashboardLayout would bounce them to /login.
  {
    path: "/tmp/complete",
    element: <TmpOnboardingPage />,
  },
  {
    path: "/",
    element: <Navigate to="/patients" replace />,
  },

  // Patients routes (with left panel)
  {
    path: "/patients",
    element: <DashboardLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="active" />,
      },
      {
        path: "active",
        element: <PatientList variant="active" />,
      },
      {
        path: "archived",
        element: <PatientList variant="archived" />,
      },
      {
        path: "flagged",
        element: <PatientList variant="flagged" />,
      },
      {
        path: "alerts",
        element: <PatientList variant="alerts" />,
      },
      {
        path: "billing",
        element: <BillingPatientList />,
      },
      {
        path: ":patientId",
        children: [
          {
            index: true,
            element: <Navigate to="overview" />,
          },
          {
            path: "overview",
            element: <PatientPage />,
          },
          {
            path: "pinned",
            element: <PatientPage />,
          },
          {
            path: "measurements",
            element: <PatientPage />,
          },
          {
            path: "spirometry",
            element: <PatientPage />,
          },
          {
            path: "labs",
            element: <PatientPage />,
          },
          {
            path: "alerts",
            element: <PatientPage />,
          },
          {
            path: "proms",
            element: <PatientPage />,
          },
          {
            path: "protocol-actions",
            element: <PatientPage />,
          },
          {
            path: "forms",
            element: <PatientPage />,
          },
          {
            path: "notes",
            element: <PatientPage />,
          },
          {
            path: "medication",
            element: <PatientPage />,
          },
          {
            path: "journal",
            element: <PatientPage />,
          },
          {
            path: "messages",
            element: <PatientPage />,
          },
          {
            path: "devices",
            element: <PatientPage />,
          },
          {
            path: "appointments",
            element: <PatientPage />,
          },
        ],
      },
    ],
  },

  // Dashboard route (left panel hidden)
  {
    path: "/dashboard",
    element: <DashboardLayout leftPanelHidden />,
    children: [
      {
        index: true,
        element: <Dashboard />,
      },
    ],
  },

  // Surveys list + assigned-list (left panel hidden) — the survey-taking page is
  // a standalone full-screen route below.
  {
    path: "/surveys",
    element: <DashboardLayout leftPanelHidden />,
    children: [
      {
        index: true,
        element: <SurveysPage />,
      },
      {
        path: "answer",
        element: <AssignedSurveysList />,
      },
    ],
  },

  // Survey answer — standalone (no auth guard, mobile webview). Do NOT nest this
  // under DashboardLayout: the mobile app loads it without a portal session and
  // the wrapper would redirect to login.
  {
    path: "/surveys/answer/:userSurveyId",
    element: <AnswerSurveyPage />,
  },

  // RAG config (left panel hidden)
  {
    path: "/rag",
    element: <DashboardLayout leftPanelHidden />,
    children: [
      {
        index: true,
        element: <RagConfigListPage />,
      },
      {
        path: "new",
        element: <RagConfigPage />,
      },
      {
        path: ":configId",
        element: <RagConfigPage />,
      },
    ],
  },

  // Protocol list (left panel hidden) — builder lives as a standalone full-screen
  // route below so the ReactFlow canvas isn't covered by the top panel.
  {
    path: "/protocol",
    element: <DashboardLayout leftPanelHidden />,
    children: [
      {
        index: true,
        element: <ProtocolList />,
      },
    ],
  },
  {
    path: "/protocol/builder",
    element: <ProtocolBuilder />,
  },
  {
    path: "/protocol/builder/:protocolId",
    element: <ProtocolBuilder />,
  },

  // Appointments route (left panel hidden)
  {
    path: "/appointments",
    element: <DashboardLayout leftPanelHidden />,
    children: [
      {
        index: true,
        element: <AppointmentsPage />,
      },
    ],
  },

  // Care Info list (left panel hidden) — article reader is a standalone full-screen
  // route below.
  {
    path: "/careinfo",
    element: <DashboardLayout leftPanelHidden />,
    children: [
      {
        index: true,
        element: <CareInfo />,
      },
    ],
  },

  // Article viewer — standalone (no auth guard, mobile webview). Do NOT nest this
  // under DashboardLayout: the mobile app loads it without a portal session and
  // the wrapper would redirect to login.
  {
    path: "/careinfo/view/:articleId",
    element: <ArticleViewer />,
  },

  // Workflows list (left panel hidden) — builder is a full-screen route below.
  {
    path: "/workflows",
    element: <DashboardLayout leftPanelHidden />,
    children: [
      {
        index: true,
        element: <WorkflowsList />,
      },
    ],
  },
  {
    path: "/workflows/builder",
    element: <WorkflowsBuilder />,
  },
  {
    path: "/workflows/builder/:workflowId",
    element: <WorkflowsBuilder />,
  },

  // Settings route (no layout)
  {
    path: "/settings",
    element: <SettingsPage />,
  },

  // Survey builder routes (no layout)
  {
    path: "/surveybuilder",
    element: <SurveyBuilder />,
  },
  {
    path: "/surveybuilder/:surveyId",
    element: <SurveyBuilder />,
  },

  // Catch-all 404
  {
    path: "*",
    element: <NotFoundRoute />,
  },
];

export const routesWithChildRoutes = [
  {
    paths: [
      "/patients/active",
      "/patients/alerts",
      "/patients/flagged",
      "/patients/archived",
      "/patients/billing",
    ],
    childRoutes: patientListChildRoutes,
  },
  {
    paths: [
      "/patients/:patientId/overview",
      "/patients/:patientId/pinned",
      "/patients/:patientId/measurements",
      "/patients/:patientId/spirometry",
      "/patients/:patientId/labs",
      "/patients/:patientId/alerts",
      "/patients/:patientId/proms",
      "/patients/:patientId/protocol-actions",
      "/patients/:patientId/forms",
      "/patients/:patientId/notes",
      "/patients/:patientId/medication",
      "/patients/:patientId/journal",
      "/patients/:patientId/messages",
      "/patients/:patientId/devices",
      "/patients/:patientId/appointments",
    ],
    childRoutes: patientPageChildRoutes,
  },
];
